import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { GeoJSONSource, Map as MapLibreMap, Marker as MapLibreMarker } from 'maplibre-gl';
import mapLibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

import { semanticColors } from '@aztec-realms/design-tokens';
import type {
  DiscoveryMarkerView,
  ExploreViewModel,
  QualityTier,
} from '@aztec-realms/shared-types';

import {
  createCodexMapStyle,
  createDiscoveryPoints,
  createExploreOverlay,
  FANTASY_REGIONS,
  type JourneyMapPosition,
  markerIconKey,
} from './codex-map';
import {
  resolveExploreMapProvider,
  resolveMapStyleResources,
  styleUsesDirectPublicOsmTiles,
} from './map-provider';
import { loadWorldNodeSprites, worldNodeSprite } from './world-node-art';
import { visualFixtureName } from '../data/vertical-slice';
import {
  constrainLocalCamera,
  localViewport,
  LOCAL_MAP_MIN_ZOOM,
  type ExploreViewport,
} from './local-map-bounds';

interface ExploreMapProps {
  snapshot: ExploreViewModel;
  selectedDiscoveryId?: string;
  revealDiscoveryId?: string;
  passive: boolean;
  qualityTier: QualityTier;
  currentPosition?: JourneyMapPosition;
  activeRoute?: readonly JourneyMapPosition[];
  journeyActive: boolean;
  playerAvatarLabel: string;
  playerAvatarUrl: string;
  locationPhase?: 'idle' | 'locating' | 'located' | 'error';
  onDiscoverySelected: (discoveryId: string) => void;
  onRequestLocation?: () => void;
  onReady?: () => void;
  onUnavailable?: () => void;
  onViewport?: (bbox: ExploreViewport) => void;
}

function motionAllowed(): boolean {
  return (
    document.documentElement.dataset.motion !== 'reduce' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function cameraDuration(): number {
  return motionAllowed() ? 220 : 0;
}

function mapProviderEnvironment() {
  return {
    VITE_MAP_STYLE_URL: import.meta.env.VITE_MAP_STYLE_URL,
    VITE_MAP_PROVIDER_NAME: import.meta.env.VITE_MAP_PROVIDER_NAME,
    VITE_MAP_PROVIDER_URL: import.meta.env.VITE_MAP_PROVIDER_URL,
  };
}

function ZoomInIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 28">
      <circle cx="12" cy="12" r="7" />
      <path d="m17.5 17.5 6 6M12 8v8M8 12h8" />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 28">
      <circle cx="12" cy="12" r="7" />
      <path d="m17.5 17.5 6 6M8 12h8" />
    </svg>
  );
}

function CompassIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="10" />
      <path d="m17.5 10.5-2 5-5 2 2-5Z" />
    </svg>
  );
}

function RecenterIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="5" />
      <path d="M14 2v5M14 21v5M2 14h5M21 14h5" />
    </svg>
  );
}

function RegionIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 28">
      <path d="m14 4 9 10-9 10L5 14Z" />
      <circle cx="14" cy="14" r="3" />
    </svg>
  );
}

function markerAccent(discovery: DiscoveryMarkerView): string {
  if (discovery.state === 'eligible') return '#ffd36a';
  if (discovery.state === 'queued') return '#35c6b5';
  if (discovery.state === 'claimed') return '#1aa68c';
  if (discovery.state === 'cooldown' || discovery.state === 'unknown') return '#a99f88';
  return '#e9cf8c';
}

function createDiscoveryMarkerImage(discovery: DiscoveryMarkerView): ImageData | undefined {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  if (!context) return undefined;
  const accent = markerAccent(discovery);
  // A small material pedestal anchors the raster object without washing over nearby streets.
  context.fillStyle = '#081312e8';
  context.strokeStyle = accent;
  context.lineWidth = 3;
  context.beginPath();
  context.ellipse(64, 101, 35, 13, 0, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  const sprite = worldNodeSprite(discovery.nodeKind ?? discovery.kind);
  if (sprite) {
    context.save();
    if (discovery.state === 'cooldown' || discovery.state === 'unknown') context.globalAlpha = 0.6;
    context.drawImage(sprite, 13, 4, 102, 102);
    context.restore();
  }
  // State is communicated by shape as well as color; artwork never determines eligibility.
  if (discovery.state === 'claimed' || discovery.state === 'eligible') {
    context.beginPath();
    context.fillStyle = '#081312';
    context.arc(99, 96, 14, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = accent;
    context.lineWidth = 4;
    context.beginPath();
    if (discovery.state === 'claimed') {
      context.moveTo(92, 96);
      context.lineTo(98, 101);
      context.lineTo(106, 90);
    } else {
      context.moveTo(96, 89);
      context.lineTo(104, 96);
      context.lineTo(96, 103);
    }
    context.stroke();
  }
  return context.getImageData(0, 0, 128, 128);
}

export function ExploreMap({
  snapshot,
  selectedDiscoveryId,
  revealDiscoveryId,
  passive,
  qualityTier,
  currentPosition,
  activeRoute = [],
  journeyActive,
  playerAvatarLabel,
  playerAvatarUrl,
  locationPhase = 'idle',
  onDiscoverySelected,
  onRequestLocation,
  onReady,
  onUnavailable,
  onViewport,
}: ExploreMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const playerMarkerRef = useRef<MapLibreMarker | null>(null);
  const passiveRef = useRef(passive);
  const currentPositionRef = useRef(currentPosition);
  const activeRouteRef = useRef(activeRoute);
  const journeyActiveRef = useRef(journeyActive);
  const playerAvatarLabelRef = useRef(playerAvatarLabel);
  const playerAvatarUrlRef = useRef(playerAvatarUrl);
  const selectionHandlerRef = useRef(onDiscoverySelected);
  const viewportHandlerRef = useRef(onViewport);
  const selectedDiscoveryIdRef = useRef(selectedDiscoveryId);
  const liveSnapshotRef = useRef(snapshot);
  const revealDiscoveryIdRef = useRef(revealDiscoveryId);
  const followingRef = useRef(true);
  const lastCenteredPositionRef = useRef<string | undefined>(undefined);
  const constrainCameraRef = useRef<(() => void) | undefined>(undefined);
  const fixtureMode =
    Boolean(visualFixtureName()) ||
    navigator.userAgent.includes('jsdom') ||
    (Boolean(
      (window as Window & { __AZR_E2E__?: { deterministic?: boolean } }).__AZR_E2E__?.deterministic,
    ) &&
      new URLSearchParams(window.location.search).get('fixture') === 'scenario-a');
  const canInitialize = Boolean(currentPosition) || fixtureMode;
  const [mapStatus, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');
  const status = canInitialize ? mapStatus : 'waiting';
  const [fallbackReason, setFallbackReason] = useState<string>();
  const [following, setFollowing] = useState(true);
  const providerPresentation = resolveExploreMapProvider(mapProviderEnvironment(), snapshot.center);
  const [activeProviderMode, setActiveProviderMode] = useState(providerPresentation.mode);
  const displayedProvider =
    activeProviderMode === 'managed-vector'
      ? providerPresentation
      : resolveExploreMapProvider({}, snapshot.center);

  useEffect(() => {
    passiveRef.current = passive;
    const markerElement = playerMarkerRef.current?.getElement();
    if (markerElement instanceof HTMLButtonElement) markerElement.disabled = passive;
  }, [passive]);

  useEffect(() => {
    liveSnapshotRef.current = snapshot;
    revealDiscoveryIdRef.current = revealDiscoveryId;
  }, [revealDiscoveryId, snapshot]);

  useLayoutEffect(() => {
    currentPositionRef.current = currentPosition;
    activeRouteRef.current = activeRoute;
    journeyActiveRef.current = journeyActive;
    constrainCameraRef.current?.();
  }, [activeRoute, currentPosition, journeyActive]);

  useEffect(() => {
    playerAvatarLabelRef.current = playerAvatarLabel;
    playerAvatarUrlRef.current = playerAvatarUrl;
    const markerElement = playerMarkerRef.current?.getElement();
    if (!(markerElement instanceof HTMLButtonElement)) return;
    markerElement.setAttribute('aria-label', `${playerAvatarLabel}, your current location`);
    const portrait = markerElement.querySelector('img');
    if (portrait) portrait.src = playerAvatarUrl;
  }, [playerAvatarLabel, playerAvatarUrl]);

  useEffect(() => {
    selectionHandlerRef.current = onDiscoverySelected;
  }, [onDiscoverySelected]);
  useEffect(() => {
    viewportHandlerRef.current = onViewport;
  }, [onViewport]);

  useEffect(() => {
    selectedDiscoveryIdRef.current = selectedDiscoveryId;
  }, [selectedDiscoveryId]);

  useEffect(() => {
    const host = hostRef.current;
    if (!canInitialize) {
      return;
    }
    if (!host || navigator.userAgent.includes('jsdom')) {
      setStatus('fallback');
      onUnavailable?.();
      return;
    }

    let cancelled = false;
    let readinessTimer: number | undefined;
    let styleRetryTimer: number | undefined;
    let viewportTimer: ReturnType<typeof setTimeout> | undefined;
    let pendingFallbackReason = 'startup-timeout';
    const finishWithFallback = (reason = pendingFallbackReason) => {
      if (cancelled) return;
      setActiveProviderMode('local-illustrated');
      setFallbackReason(reason);
      setStatus('fallback');
      onUnavailable?.();
    };

    setFallbackReason(undefined);
    setStatus('loading');

    void Promise.all([
      import('maplibre-gl'),
      import('maplibre-gl/dist/maplibre-gl.css'),
      loadWorldNodeSprites(),
    ])
      .then(([maplibregl]) => {
        if (cancelled) return;
        const setupSnapshot = liveSnapshotRef.current;
        maplibregl.setWorkerUrl(mapLibreWorkerUrl);
        const provider = resolveExploreMapProvider(mapProviderEnvironment(), setupSnapshot.center);
        let localStyleAttempted = provider.mode === 'local-illustrated';
        let layersInstalled = false;
        let managedStyleRetries = 0;
        setActiveProviderMode(provider.mode);
        const map = new maplibregl.Map({
          container: host,
          center: [...(currentPositionRef.current?.coordinates ?? setupSnapshot.center)],
          zoom: 13.25,
          minZoom: LOCAL_MAP_MIN_ZOOM,
          maxZoom: 22,
          pitch: 0,
          maxPitch: 36,
          attributionControl: false,
          cooperativeGestures: false,
          dragRotate: false,
          fadeDuration: qualityTier === 'low' ? 0 : 180,
          renderWorldCopies: false,
        });
        mapRef.current = map;
        const loadProviderStyle = () => {
          // transformStyle waits for an existing style to finish loading. A failed
          // style never does, so detach it before a bounded fresh-load retry.
          if (managedStyleRetries > 0) map.setStyle(null);
          map.setStyle(provider.style, {
            // A failed first style has no usable stylesheet to diff against.
            // Recreate its style lifecycle when retrying the same provider.
            diff: false,
            transformStyle: (_previous, next) =>
              resolveMapStyleResources(
                next,
                typeof provider.style === 'string' ? provider.style : window.location.href,
                window.location.href,
              ),
          });
        };
        let constraining = false;
        let cameraSafe = false;
        const constrainCamera = () => {
          if (constraining || cancelled) return;
          constraining = true;
          try {
            const origin =
              currentPositionRef.current?.coordinates ??
              (fixtureMode ? setupSnapshot.center : undefined);
            cameraSafe = Boolean(origin && constrainLocalCamera(map, origin));
            // Fail closed for an unsupported projection/viewport instead of showing remote ground.
            host.style.visibility = cameraSafe ? '' : 'hidden';
          } catch {
            cameraSafe = false;
            host.style.visibility = 'hidden';
          } finally {
            constraining = false;
          }
        };
        constrainCameraRef.current = constrainCamera;
        map.on('move', constrainCamera);
        map.on('resize', constrainCamera);
        constrainCamera();
        let lastViewport: string | undefined;
        const reportViewport = () => {
          clearTimeout(viewportTimer);
          viewportTimer = setTimeout(() => {
            if (cancelled || passiveRef.current || !cameraSafe) return;
            const bounds = map.getBounds();
            const viewport = localViewport([
              bounds.getWest(),
              bounds.getSouth(),
              bounds.getEast(),
              bounds.getNorth(),
            ]);
            const key = JSON.stringify(viewport);
            if (key === lastViewport) return;
            lastViewport = key;
            viewportHandlerRef.current?.(viewport);
          }, 400);
        };
        map.on('moveend', reportViewport);
        map.on('load', reportViewport);
        const playerMarkerElement = document.createElement('button');
        playerMarkerElement.className = 'explore-player-marker';
        playerMarkerElement.type = 'button';
        playerMarkerElement.setAttribute(
          'aria-label',
          `${playerAvatarLabelRef.current}, your current location`,
        );
        playerMarkerElement.disabled = passiveRef.current;
        playerMarkerElement.dataset.journeyActive = journeyActiveRef.current ? 'true' : 'false';
        const playerPortrait = document.createElement('img');
        playerPortrait.alt = '';
        playerPortrait.src = playerAvatarUrlRef.current;
        // Decode the player's actual portrait before revealing the first map frame.
        const portraitReady = playerPortrait.decode();
        void portraitReady.catch(() => undefined);
        playerMarkerElement.append(playerPortrait);
        playerMarkerElement.addEventListener('click', (event) => {
          event.stopPropagation();
          const position = currentPositionRef.current;
          if (!position || passiveRef.current) return;
          followingRef.current = true;
          setFollowing(true);
          map.easeTo({
            center: [position.coordinates[0], position.coordinates[1]],
            zoom: Math.max(map.getZoom(), 15.25),
            bearing: 0,
            duration: cameraDuration(),
          });
        });
        const playerMarker = new maplibregl.Marker({
          anchor: 'bottom',
          element: playerMarkerElement,
        });
        playerMarkerRef.current = playerMarker;
        if (currentPositionRef.current) {
          playerMarkerElement.dataset.positionKey = currentPositionRef.current.coordinates
            .map((coordinate) => coordinate.toFixed(6))
            .join(',');
          playerMarkerElement.dataset.positionRecordedAt = currentPositionRef.current.recordedAt;
          playerMarker
            .setLngLat([
              currentPositionRef.current.coordinates[0],
              currentPositionRef.current.coordinates[1],
            ])
            .addTo(map);
        }
        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'top-right');
        const releaseFollowOnManualMove = (event: { originalEvent?: unknown }) => {
          if (!event.originalEvent || !currentPositionRef.current) return;
          followingRef.current = false;
          setFollowing(false);
        };
        map.on('dragstart', releaseFollowOnManualMove);
        map.on('zoomstart', releaseFollowOnManualMove);
        map.on('pitchstart', releaseFollowOnManualMove);
        map.on('error', () => {
          if (localStyleAttempted || map.getSource('explore-knowledge')) return;
          // One bounded retry of the same provider; never switch usable streets to
          // illustrated terrain for a transient initial request failure.
          if (styleRetryTimer !== undefined) return;
          if (managedStyleRetries === 0) {
            managedStyleRetries += 1;
            styleRetryTimer = window.setTimeout(() => {
              styleRetryTimer = undefined;
              if (!cancelled && !layersInstalled) loadProviderStyle();
            }, 1_000);
            return;
          }
          // Only an actual provider fallback invalidates first-scene readiness. A recovered
          // source/tile request must not permanently poison an otherwise complete live map.
          pendingFallbackReason = 'managed-style-error';
          localStyleAttempted = true;
          setActiveProviderMode('local-illustrated');
          map.setStyle(createCodexMapStyle(liveSnapshotRef.current.center));
        });

        const installExploreLayers = () => {
          if (cancelled || layersInstalled) return;
          layersInstalled = true;

          const currentSnapshot = liveSnapshotRef.current;
          for (const discovery of currentSnapshot.discoveries) {
            const imageKey = markerIconKey(discovery);
            if (map.hasImage(imageKey)) continue;
            const markerImage = createDiscoveryMarkerImage(discovery);
            if (markerImage) map.addImage(imageKey, markerImage, { pixelRatio: 2 });
          }
          if (cancelled) return;

          map.addSource('explore-knowledge', {
            type: 'geojson',
            data: createExploreOverlay(
              currentSnapshot,
              currentPositionRef.current,
              activeRouteRef.current,
              journeyActiveRef.current,
            ),
          });
          map.addSource('explore-discoveries', {
            type: 'geojson',
            data: createDiscoveryPoints(currentSnapshot, revealDiscoveryIdRef.current),
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: qualityTier === 'low' ? 64 : 54,
          });

          // Knowledge-state pigment belongs to the map's terrain, not on top of its streets.
          // Keep these sparse carved halos below buildings, roads and labels so local geography
          // always remains the dominant navigational signal.
          const knowledgeUnderlayAnchor = map.getLayer('buildings') ? 'buildings' : undefined;
          const addKnowledgeUnderlay = (layer: Parameters<typeof map.addLayer>[0]) =>
            map.addLayer(layer, knowledgeUnderlayAnchor);

          addKnowledgeUnderlay({
            id: 'knowledge-cell-shadow',
            type: 'fill',
            source: 'explore-knowledge',
            filter: [
              'all',
              ['==', ['get', 'kind'], 'knowledge-cell'],
              ['in', ['get', 'knowledge'], ['literal', ['discovered', 'seen']]],
            ],
            paint: {
              'fill-color': semanticColors.backgroundDeep,
              'fill-opacity': 0.035,
              'fill-translate': [1, 2],
            },
          });
          addKnowledgeUnderlay({
            id: 'knowledge-cell-wash',
            type: 'fill',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'knowledge-cell'],
            paint: {
              'fill-color': [
                'match',
                ['get', 'knowledge'],
                'discovered',
                '#2c806a',
                'seen',
                '#927f55',
                '#b8a984',
              ],
              'fill-opacity': [
                'match',
                ['get', 'knowledge'],
                'discovered',
                0.085,
                'seen',
                0.045,
                0.008,
              ],
            },
          });
          addKnowledgeUnderlay({
            id: 'fog-of-knowledge',
            type: 'fill',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'knowledge'], 'unknown'],
            paint: {
              'fill-color': '#d8c7a4',
              'fill-opacity': 0.012,
            },
          });
          addKnowledgeUnderlay({
            id: 'fog-cloud-edge',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'knowledge-cell'],
            paint: {
              'line-color': [
                'match',
                ['get', 'knowledge'],
                'discovered',
                '#6eb797',
                'seen',
                '#bda56a',
                '#9f8c61',
              ],
              'line-width': ['match', ['get', 'knowledge'], 'discovered', 1.9, 'seen', 1, 0.7],
              'line-opacity': ['match', ['get', 'knowledge'], 'discovered', 0.34, 'seen', 0.2, 0.1],
              'line-dasharray': [0.55, 2.3],
            },
          });
          map.addLayer({
            id: 'knowledge-current-shadow',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'energy-link'],
            paint: {
              'line-color': semanticColors.backgroundDeep,
              'line-width': ['match', ['get', 'state'], 'awakened', 12, 'ready', 9, 6],
              'line-opacity': 0.72,
              'line-blur': 1.8,
            },
          });
          map.addLayer({
            id: 'knowledge-current-bed',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'energy-link'],
            paint: {
              'line-color': [
                'match',
                ['get', 'state'],
                'awakened',
                semanticColors.solarGold,
                'ready',
                semanticColors.turquoise,
                'attuned',
                '#a9873f',
                '#776f5e',
              ],
              'line-width': ['match', ['get', 'state'], 'awakened', 5.5, 'ready', 4, 2],
              'line-opacity': ['match', ['get', 'state'], 'dormant', 0.34, 0.78],
              'line-dasharray': [1.2, 1.8],
            },
          });
          map.addLayer({
            id: 'knowledge-current-energy',
            type: 'line',
            source: 'explore-knowledge',
            filter: [
              'all',
              ['==', ['get', 'kind'], 'energy-link'],
              ['in', ['get', 'state'], ['literal', ['ready', 'awakened']]],
            ],
            paint: {
              'line-color': '#fff0a6',
              'line-width': 1.5,
              'line-opacity': 0.96,
              'line-blur': 0.4,
              'line-dasharray': [0.2, 1.4],
            },
          });
          map.addLayer({
            id: 'world-route-shadow',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'world-route'],
            paint: {
              'line-color': semanticColors.backgroundDeep,
              'line-width': ['match', ['get', 'state'], 'active', 12, 7],
              'line-opacity': 0.68,
              'line-blur': 1,
            },
          });
          map.addLayer({
            id: 'world-route-path',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'world-route'],
            paint: {
              'line-color': [
                'match',
                ['get', 'state'],
                'active',
                semanticColors.solarGold,
                'completed',
                semanticColors.turquoise,
                '#d6bf82',
              ],
              'line-width': ['match', ['get', 'state'], 'active', 5, 2.5],
              'line-opacity': ['match', ['get', 'state'], 'available', 0.5, 0.92],
              'line-dasharray': [1, 1.4],
            },
          });
          map.addLayer({
            id: 'world-route-stops',
            type: 'circle',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'world-route-stop'],
            paint: {
              'circle-radius': ['match', ['get', 'stopState'], 'next', 9, 'visited', 7, 5],
              'circle-color': [
                'match',
                ['get', 'stopState'],
                'visited',
                semanticColors.turquoise,
                'next',
                semanticColors.solarGold,
                semanticColors.backgroundDeep,
              ],
              'circle-stroke-color': '#fff0a6',
              'circle-stroke-width': ['match', ['get', 'stopState'], 'next', 3, 1.5],
              'circle-opacity': ['match', ['get', 'routeState'], 'available', 0.68, 0.96],
            },
          });
          map.addLayer({
            id: 'active-route-shadow',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'active-route'],
            paint: {
              'line-color': semanticColors.backgroundDeep,
              'line-width': 13,
              'line-opacity': 0.58,
              'line-blur': 1.2,
            },
          });
          map.addLayer({
            id: 'active-route-gold-bed',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'active-route'],
            paint: {
              'line-color': '#8f6c2f',
              'line-width': 6.5,
              'line-opacity': 0.74,
            },
          });
          map.addLayer({
            id: 'active-route',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'active-route'],
            paint: {
              'line-color': semanticColors.solarGold,
              'line-width': 2.8,
              'line-opacity': ['case', ['get', 'active'], 0.98, 0.76],
              'line-dasharray': [0.3, 1.45],
            },
          });
          map.addLayer({
            id: 'route-codex-stamps-outer',
            type: 'circle',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'route-step'],
            paint: {
              'circle-radius': ['case', ['get', 'major'], 7, 5.5],
              'circle-color': semanticColors.backgroundDeep,
              'circle-opacity': 0.88,
              'circle-stroke-color': '#b88934',
              'circle-stroke-width': 1.5,
            },
          });
          map.addLayer({
            id: 'route-codex-stamps',
            type: 'circle',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'route-step'],
            paint: {
              'circle-radius': ['case', ['get', 'major'], 3.5, 2.5],
              'circle-color': semanticColors.solarGold,
              'circle-stroke-color': semanticColors.backgroundDeep,
              'circle-stroke-width': 2,
            },
          });
          map.addLayer({
            id: 'interaction-radius-fill',
            type: 'fill',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'interaction-radius'],
            paint: {
              'fill-color': semanticColors.turquoise,
              'fill-opacity': ['case', ['get', 'active'], 0.13, 0.08],
            },
          });
          map.addLayer({
            id: 'interaction-radius-edge',
            type: 'line',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'interaction-radius'],
            paint: {
              'line-color': semanticColors.turquoise,
              'line-width': 2,
              'line-opacity': ['case', ['get', 'active'], 0.86, 0.56],
              'line-dasharray': [1.2, 1.4],
            },
          });
          map.addLayer({
            id: 'current-position-accuracy',
            type: 'circle',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'current-position'],
            paint: {
              'circle-radius': 18,
              'circle-color': semanticColors.turquoise,
              'circle-opacity': 0.16,
              'circle-stroke-color': semanticColors.turquoise,
              'circle-stroke-width': 1.5,
            },
          });
          map.addLayer({
            id: 'current-position',
            type: 'circle',
            source: 'explore-knowledge',
            filter: ['==', ['get', 'kind'], 'current-position'],
            paint: {
              'circle-radius': 6,
              'circle-color': [
                'case',
                ['get', 'active'],
                semanticColors.turquoise,
                semanticColors.solarGold,
              ],
              'circle-stroke-color': semanticColors.backgroundDeep,
              'circle-stroke-width': 3,
            },
          });
          map.addLayer({
            id: 'discovery-cluster-aura',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['has', 'point_count'],
            paint: {
              'circle-radius': ['step', ['get', 'point_count'], 23, 8, 29, 20, 35],
              'circle-color': semanticColors.solarGold,
              'circle-opacity': 0.22,
              'circle-blur': 0.45,
            },
          });
          map.addLayer({
            id: 'discovery-cluster-core',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['has', 'point_count'],
            paint: {
              'circle-radius': ['step', ['get', 'point_count'], 14, 8, 17, 20, 20],
              'circle-color': semanticColors.backgroundPanel,
              'circle-stroke-color': semanticColors.solarGold,
              'circle-stroke-width': 3,
            },
          });
          if (map.getStyle().glyphs) {
            map.addLayer({
              id: 'discovery-cluster-count',
              type: 'symbol',
              source: 'explore-discoveries',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-size': 12,
                'text-font': ['Noto Sans Regular'],
                'text-allow-overlap': true,
              },
              paint: {
                'text-color': '#fff0a6',
                'text-halo-color': semanticColors.backgroundDeep,
                'text-halo-width': 1.5,
              },
            });
          }
          map.addLayer({
            id: 'discovery-reveal-pigment',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'reveal'], true]],
            paint: {
              'circle-radius': 68,
              'circle-color': '#2b8a71',
              'circle-opacity': 0.16,
              'circle-blur': 0.72,
              'circle-stroke-color': '#a9873f',
              'circle-stroke-width': 1.4,
            },
          });
          map.addLayer({
            id: 'discovery-reveal-halo',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'reveal'], true]],
            paint: {
              'circle-radius': 44,
              'circle-color': semanticColors.solarGold,
              'circle-opacity': 0.16,
              'circle-blur': 0.72,
            },
          });
          map.addLayer({
            id: 'discovery-reveal-pulse',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'reveal'], true]],
            paint: {
              'circle-radius': 31,
              'circle-color': semanticColors.solarGold,
              'circle-opacity': 0.2,
              'circle-blur': 0.32,
              'circle-stroke-color': semanticColors.solarGold,
              'circle-stroke-width': 3,
            },
          });
          map.addLayer({
            id: 'discovery-selection',
            type: 'circle',
            source: 'explore-discoveries',
            filter: [
              'all',
              ['!', ['has', 'point_count']],
              ['==', ['get', 'id'], selectedDiscoveryIdRef.current ?? ''],
            ],
            paint: {
              'circle-radius': 31,
              'circle-color': 'rgba(0,0,0,0)',
              'circle-stroke-color': semanticColors.solarGold,
              'circle-stroke-width': 3,
              'circle-opacity': 0.95,
            },
          });
          map.addLayer({
            id: 'discovery-state-core',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['!', ['has', 'point_count']],
            paint: {
              'circle-radius': ['case', ['==', ['get', 'rarity'], 'rare'], 18, 16],
              'circle-color': [
                'match',
                ['get', 'state'],
                'eligible',
                semanticColors.solarGold,
                'queued',
                semanticColors.turquoise,
                'claimed',
                semanticColors.jade,
                'cooldown',
                semanticColors.sandstone,
                '#d8c7a4',
              ],
              'circle-opacity': 0.86,
              'circle-stroke-color': semanticColors.backgroundDeep,
              'circle-stroke-width': 3,
            },
          });
          map.addLayer({
            id: 'discovery-weave-halo',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'weaveRole'], '']],
            paint: {
              'circle-radius': ['match', ['get', 'weaveRole'], 'woven', 29, 25],
              'circle-color': 'rgba(0,0,0,0)',
              'circle-stroke-color': [
                'match',
                ['get', 'weaveRole'],
                'origin',
                semanticColors.turquoise,
                'destination',
                semanticColors.solarGold,
                '#fff0a6',
              ],
              'circle-stroke-width': 3,
              'circle-opacity': 0.92,
              'circle-blur': 0.15,
            },
          });
          map.addLayer({
            id: 'discovery-symbols',
            type: 'symbol',
            source: 'explore-discoveries',
            filter: ['!', ['has', 'point_count']],
            layout: {
              'icon-image': ['get', 'icon'],
              'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.74, 14, 1],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            },
          });
          map.addLayer({
            id: 'discovery-hit-area',
            type: 'circle',
            source: 'explore-discoveries',
            filter: ['!', ['has', 'point_count']],
            paint: { 'circle-radius': 24, 'circle-opacity': 0.001 },
          });

          map.on('click', 'discovery-hit-area', (event) => {
            if (passiveRef.current) return;
            const id = event.features?.[0]?.properties.id;
            if (typeof id === 'string') selectionHandlerRef.current(id);
          });
          map.on('mouseenter', 'discovery-hit-area', () => {
            if (!passiveRef.current) map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', 'discovery-hit-area', () => {
            map.getCanvas().style.cursor = '';
          });
          map.on('click', 'discovery-cluster-core', (event) => {
            if (passiveRef.current) return;
            const feature = event.features?.[0];
            const clusterId = feature?.properties.point_count
              ? Number(feature.properties.cluster_id)
              : undefined;
            const coordinates =
              feature?.geometry.type === 'Point' ? feature.geometry.coordinates : undefined;
            const source = map.getSource('explore-discoveries') as GeoJSONSource | undefined;
            if (!source || clusterId === undefined || !coordinates) return;
            void source.getClusterExpansionZoom(clusterId).then((zoom) => {
              map.easeTo({
                center: [
                  coordinates[0] ?? map.getCenter().lng,
                  coordinates[1] ?? map.getCenter().lat,
                ],
                zoom: Math.min(17, zoom),
                duration: cameraDuration(),
              });
            });
          });
          map.on('mouseenter', 'discovery-cluster-core', () => {
            if (!passiveRef.current) map.getCanvas().style.cursor = 'zoom-in';
          });
          map.on('mouseleave', 'discovery-cluster-core', () => {
            map.getCanvas().style.cursor = '';
          });

          const markReadyWhenIdle = () => {
            if (pendingFallbackReason !== 'startup-timeout' || !cameraSafe) {
              finishWithFallback(cameraSafe ? pendingFallbackReason : 'camera-unavailable');
              return;
            }
            void Promise.all([portraitReady, document.fonts.ready])
              .then(() => {
                if (cancelled) return;
                map.once('render', () => {
                  if (cancelled) return;
                  if (readinessTimer !== undefined) window.clearTimeout(readinessTimer);
                  setStatus('ready');
                  onReady?.();
                });
                map.triggerRepaint();
              })
              .catch(() => finishWithFallback('portrait-load-error'));
          };
          if (map.loaded()) markReadyWhenIdle();
          else map.once('idle', markReadyWhenIdle);
        };

        const styleReady = () => {
          if (styleRetryTimer !== undefined) window.clearTimeout(styleRetryTimer);
          styleRetryTimer = undefined;
          if (
            provider.mode === 'managed-vector' &&
            !localStyleAttempted &&
            styleUsesDirectPublicOsmTiles(map.getStyle())
          ) {
            localStyleAttempted = true;
            setActiveProviderMode('local-illustrated');
            map.setStyle(createCodexMapStyle(liveSnapshotRef.current.center));
            return;
          }
          window.requestAnimationFrame(() => {
            try {
              installExploreLayers();
            } catch {
              finishWithFallback('layer-install-error');
            }
          });
        };
        // `style.load` is the MapLibre lifecycle boundary that guarantees all resources referenced
        // by the style have loaded. `styledata` can fire earlier while `isStyleLoaded()` is still
        // false and may not fire again for an entirely local style.
        map.on('style.load', styleReady);
        // Arm the timeout before setting the style so a synchronously parsed local style cannot
        // race with timer creation. A managed provider follows the same readiness path.
        readinessTimer = window.setTimeout(() => finishWithFallback(), 30_000);
        loadProviderStyle();
      })
      .catch(() => finishWithFallback('module-load-error'));

    return () => {
      cancelled = true;
      if (readinessTimer !== undefined) window.clearTimeout(readinessTimer);
      if (styleRetryTimer !== undefined) window.clearTimeout(styleRetryTimer);
      clearTimeout(viewportTimer);
      playerMarkerRef.current?.remove();
      playerMarkerRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
      constrainCameraRef.current = undefined;
      host.replaceChildren();
    };
  }, [canInitialize, fixtureMode, onReady, onUnavailable, qualityTier]);

  useEffect(() => {
    const map = mapRef.current;
    const source = map?.getSource('explore-knowledge') as GeoJSONSource | undefined;
    source?.setData(createExploreOverlay(snapshot, currentPosition, activeRoute, journeyActive));
  }, [activeRoute, currentPosition, journeyActive, snapshot, status]);

  useEffect(() => {
    const map = mapRef.current;
    const source = map?.getSource('explore-discoveries') as GeoJSONSource | undefined;
    if (!map || !source) return;
    for (const discovery of snapshot.discoveries) {
      const imageKey = markerIconKey(discovery);
      if (map.hasImage(imageKey)) continue;
      const markerImage = createDiscoveryMarkerImage(discovery);
      if (markerImage) map.addImage(imageKey, markerImage, { pixelRatio: 2 });
    }
    source.setData(createDiscoveryPoints(snapshot, revealDiscoveryId));
  }, [revealDiscoveryId, snapshot, status]);

  useEffect(() => {
    const map = mapRef.current;
    const marker = playerMarkerRef.current;
    if (!map || !marker || !currentPosition) return;
    marker.getElement().dataset.journeyActive = journeyActive ? 'true' : 'false';
    marker.getElement().dataset.positionKey = currentPosition.coordinates
      .map((coordinate) => coordinate.toFixed(6))
      .join(',');
    marker.getElement().dataset.positionRecordedAt = currentPosition.recordedAt;
    marker.setLngLat([currentPosition.coordinates[0], currentPosition.coordinates[1]]);
    if (!marker.getElement().isConnected) marker.addTo(map);
  }, [currentPosition, journeyActive, status]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || status !== 'ready' || !currentPosition) return;
    if (lastCenteredPositionRef.current === currentPosition.recordedAt) return;
    lastCenteredPositionRef.current = currentPosition.recordedAt;
    constrainCameraRef.current?.();
    if (!followingRef.current) return;
    followingRef.current = true;
    setFollowing(true);
    map.easeTo({
      center: [currentPosition.coordinates[0], currentPosition.coordinates[1]],
      zoom: Math.max(map.getZoom(), 15.25),
      bearing: 0,
      pitch: qualityTier === 'low' ? 0 : 18,
      duration: cameraDuration(),
    });
  }, [currentPosition, journeyActive, qualityTier, status]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('discovery-selection')) return;
    map.setFilter('discovery-selection', [
      'all',
      ['!', ['has', 'point_count']],
      ['==', ['get', 'id'], selectedDiscoveryId ?? ''],
    ]);
  }, [selectedDiscoveryId, status]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (passive) {
      map.dragPan.disable();
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
    } else {
      map.dragPan.enable();
      map.scrollZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      map.doubleClickZoom.enable();
      map.touchZoomRotate.enable();
    }
  }, [passive, status]);

  useEffect(() => {
    const map = mapRef.current;
    if (
      status !== 'ready' ||
      !map ||
      !revealDiscoveryId ||
      qualityTier === 'low' ||
      !motionAllowed()
    ) {
      return;
    }
    let animationFrame = 0;
    const startedAt = performance.now();
    const animatePulse = (time: number) => {
      const layer = map.getLayer('discovery-reveal-pulse');
      if (!layer) return;
      const progress = ((time - startedAt) % 1_800) / 1_800;
      map.setPaintProperty('discovery-reveal-pulse', 'circle-radius', 27 + progress * 26);
      map.setPaintProperty(
        'discovery-reveal-pulse',
        'circle-opacity',
        Math.max(0.035, 0.38 * (1 - progress)),
      );
      if (map.getLayer('discovery-reveal-halo')) {
        map.setPaintProperty(
          'discovery-reveal-halo',
          'circle-opacity',
          0.08 + Math.sin(progress * Math.PI) * 0.12,
        );
      }
      animationFrame = window.requestAnimationFrame(animatePulse);
    };
    animationFrame = window.requestAnimationFrame(animatePulse);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [qualityTier, revealDiscoveryId, status]);

  const changeZoom = useCallback((direction: 'in' | 'out') => {
    const map = mapRef.current;
    if (!map || passiveRef.current) return;
    if (direction === 'in') map.zoomIn({ duration: cameraDuration() });
    else map.zoomOut({ duration: cameraDuration() });
  }, []);

  const resetBearing = useCallback(() => {
    const map = mapRef.current;
    if (!map || passiveRef.current) return;
    map.easeTo({ bearing: 0, duration: cameraDuration() });
  }, []);

  const centerOnPosition = useCallback(() => {
    const map = mapRef.current;
    if (passiveRef.current) return;
    if (!currentPosition) {
      onRequestLocation?.();
      return;
    }
    if (!map) return;
    followingRef.current = true;
    setFollowing(true);
    map.easeTo({
      center: [currentPosition.coordinates[0], currentPosition.coordinates[1]],
      bearing: 0,
      pitch: qualityTier === 'low' ? 0 : 18,
      duration: cameraDuration(),
    });
  }, [currentPosition, onRequestLocation, qualityTier]);

  const returnToRegion = useCallback(() => {
    const map = mapRef.current;
    if (!map || passiveRef.current) return;
    followingRef.current = false;
    setFollowing(false);
    map.easeTo({
      center: [...(currentPosition?.coordinates ?? snapshot.center)],
      zoom: 13.25,
      bearing: 0,
      pitch: 0,
      duration: cameraDuration(),
    });
  }, [currentPosition, snapshot.center]);

  return (
    <section
      aria-label={`${snapshot.regionName} exploration map`}
      className="explore-map"
      data-passive={passive || undefined}
      data-map-provider={activeProviderMode}
      data-following={currentPosition ? following : undefined}
      data-location-phase={locationPhase}
      data-position-source={currentPosition ? (journeyActive ? 'journey' : 'browser') : undefined}
      data-quality={qualityTier}
      data-render-fallback-reason={status === 'fallback' ? fallbackReason : undefined}
      data-render-status={status}
    >
      <div className="explore-map__host" ref={hostRef} />
      <span aria-hidden="true" className="explore-map__water-shimmer" />
      <span aria-hidden="true" className="explore-map__codex-grain" />
      <div className="explore-map__region-seal">
        <span aria-hidden="true" className="explore-map__region-mark">
          ◈
        </span>
        <span>
          <small>{displayedProvider.notice}</small>
          <strong>{snapshot.regionName}</strong>
        </span>
      </div>
      <div aria-label="Fantasy regions" className="explore-map__region-list">
        {FANTASY_REGIONS.map((region) => (
          <span
            data-active={region.displayName === snapshot.regionName || undefined}
            key={region.code}
          >
            {region.displayName}
          </span>
        ))}
      </div>
      {displayedProvider.mode === 'local-illustrated' ? (
        <div className="explore-map__attribution">
          {displayedProvider.attribution.length > 0
            ? displayedProvider.attribution.map((item) => (
                <a href={item.href} key={item.href} rel="noreferrer" target="_blank">
                  {item.label}
                </a>
              ))
            : displayedProvider.notice}
        </div>
      ) : null}
      <a
        className="explore-map__world-data"
        href="/world-data.html"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="World data and source credits (opens in a new tab)"
      >
        World data
      </a>
      <div aria-label="Map view controls" className="explore-map__controls" role="group">
        <button
          aria-label="Zoom further into the street map"
          disabled={passive || status !== 'ready'}
          onClick={() => changeZoom('in')}
          type="button"
        >
          <ZoomInIcon />
        </button>
        <button
          aria-label="Zoom out from the street map"
          disabled={passive || status !== 'ready'}
          onClick={() => changeZoom('out')}
          type="button"
        >
          <ZoomOutIcon />
        </button>
        <button
          aria-label="Reset map direction to north"
          disabled={passive || status !== 'ready'}
          onClick={resetBearing}
          type="button"
        >
          <CompassIcon />
        </button>
        <button
          aria-label={
            currentPosition
              ? following
                ? 'Center on my browser location, currently following'
                : 'Resume following my browser location'
              : 'Use my browser location'
          }
          data-active={currentPosition && following ? 'true' : undefined}
          disabled={
            passive ||
            (status !== 'ready' && status !== 'waiting') ||
            locationPhase === 'locating' ||
            (journeyActive && !currentPosition)
          }
          onClick={centerOnPosition}
          type="button"
        >
          <RecenterIcon />
        </button>
        <button
          aria-label="View nearby streets"
          disabled={passive || status !== 'ready'}
          onClick={returnToRegion}
          type="button"
        >
          <RegionIcon />
        </button>
      </div>
      {status === 'loading' && (
        <span className="canvas-status">Painting known paths into the Codex…</span>
      )}
      {status === 'waiting' && (
        <span className="canvas-status">Use your browser location to view nearby streets.</span>
      )}
      {status === 'fallback' && (
        <div
          className="codex-map-fallback"
          role="img"
          aria-label="A codex-inspired water map with garden islands, a discovery trail and fogged regions"
        >
          <span className="codex-map-fallback__island codex-map-fallback__island--north" />
          <span className="codex-map-fallback__island codex-map-fallback__island--west" />
          <span className="codex-map-fallback__island codex-map-fallback__island--south" />
          {snapshot.discoveries.map((discovery, index) => (
            <button
              aria-label={`Select ${discovery.title}, ${discovery.state}`}
              className="codex-map-fallback__marker"
              data-kind={discovery.kind}
              data-reveal={discovery.id === revealDiscoveryId || undefined}
              data-state={discovery.state}
              disabled={passive}
              key={discovery.id}
              onClick={() => onDiscoverySelected(discovery.id)}
              style={
                {
                  '--marker-x': `${[43, 68, 25][index % 3]}%`,
                  '--marker-y': `${[54, 31, 70][index % 3]}%`,
                } as CSSProperties
              }
              type="button"
            >
              <span aria-hidden="true">
                {discovery.kind === 'glyph_fragment'
                  ? '✦'
                  : discovery.kind === 'celestial'
                    ? '☼'
                    : '⌂'}
              </span>
            </button>
          ))}
        </div>
      )}
      <span className="ar-visually-hidden" role="status">
        {status === 'ready'
          ? `Exploration map ready.${
              currentPosition
                ? ` Current browser position accuracy ${Math.round(currentPosition.accuracyM)} metres.${
                    journeyActive
                      ? following
                        ? ' Journey camera is following.'
                        : ' Manual map movement paused camera follow.'
                      : ' This orientation point is not a Journey and earns no reward.'
                  }`
                : ''
            }`
          : 'A navigable discovery list is available with the map.'}
      </span>
    </section>
  );
}
